<template>
  <div>
    <vx-card id="camera-view" class="mb-base overflow-hidden" v-on="$listeners">
      <template slot="no-body">
        <div id="camera-center-area" class="vs-con-loading__container">

          <!-- 上部エリア: タイトル、アクションボタンなど -->
          <camera-header-view :camera="camera" :mode="mode" :to-list-clicked="toListClicked"
            :target-date-ymd-slash="targetDateYmdSlash" :target-hour="targetHour" :onvif-enable="onvifEnable"
            :onvif-active="onvifActive" :can-prev-date="canPrevDate" :can-next-date="canNextDate"
            :increment-target-date="incrementTargetDate" :config-day-picker="configDayPicker"
            :target-date-ymd="targetDateYmd" :change-mode="changeMode" :can-realtime="canRealtime"
            :is-ecomott-cam="isEcomottCam" @update:onvifActive="onvifActive = $event"
            @update:targetDateYmd="targetDateYmd = $event" :is-history-playback="isHistoryPlayback" />

          <!-- 中央エリア: 画像とコントロールボタン -->
          <div :style="{ height: `calc(100vh - ${videoPlayerAreaHeightOffset})` }">
            <div v-if="!(mode === 'history' && camera && camera.movieType === CAMERA_MOVIE_TYPE.CAMERA)">
              <div class="flex w-full justify-between items-center">


                <camera-main-view ref="cameraMainView" :camera="camera" :camera-msg="cameraMsg"
                  :has-video-error="hasVideoError" :retry-text="retryText" :connect="connect" :is-hyke-cam="isHykeCam"
                  :mode="mode" :onvif-enable="onvifEnable" :onvif-active="onvifActive"
                  :on-fullscreen-change="onFullscreenChange"
                  :video-player-area-height-offset="videoPlayerAreaHeightOffset" :onvif-area-size="onvifAreaSize"
                  :video-player-width-by-height="videoPlayerWidthByHeight" :player-options="mainPlayerOptions"
                  :zoom-size="zoomSize" :zoom-list="zoomList" :target-zoom="targetZoom" :zoom-clicked="zoomClicked"
                  :current-moment-value-of="currentMomentValueOf" :play-start-moment-value-of="playStartMomentValueOf"
                  :fps="fps" :thumbnails="thumbnails" :on-play="onPlay" :on-change-volume="onChangeVolume"
                  :set-f-p-s="setFPS" :on-change-seek="onChangeSeek" :set-fullscreen="setFullscreen"
                  :hyke-cam-height-px="hykeCamHeightPx" :hyke-cam-loader-show="hykeCamLoaderShow"
                  :hyke-cam-loader-width-px="hykeCamLoaderWidthPx" :hyke-cam-loader-height-px="hykeCamLoaderHeightPx"
                  :hyke-cam-loader-top-px="hykeCamLoaderTopPx" :hyke-cam-loader-left-px="hykeCamLoaderLeftPx"
                  :hyke-cam-screen="hykeCamScreen" :on-player-readied="onPlayerReadied" :on-player-play="onPlayerPlay"
                  :on-player-pause="onPlayerPause" :on-player-error="onPlayerError" :on-player-ended="onPlayerEnded"
                  :is-web-r-t-c="isWebRTC" :webrtc-url="webrtcUrl" :on-video-click="onVideoClick" :is-history-playback="isHistoryPlayback"/>


                <!-- onvifのみ: 垂直移動ゲージ -->
                <onvif-camera-control-view v-if="onvifActive" :onvif-active="onvifActive"
                  :onvif-area-size="onvifAreaSize" :video-player-area-height-offset="videoPlayerAreaHeightOffset"
                  :camera="camera" :camera-large-move="cameraLargeMove" :camera-small-move="cameraSmallMove"
                  :camera-zoom-move="cameraZoomMove" :onvif-ptz-by-scroll="onvifPtzByScroll"
                  :onvif-goto-home-position="onvifGotoHomePosition" :on-change-volume="onChangeVolume"
                  :set-fullscreen="setFullscreen" :volume="volume" @update:volume="volume = $event"
                  :AISEA_COLOR="AISEA_COLOR" />
              </div>

            </div>
            <div v-else id="camera-play-back-area" class="flex w-full justify-center items-start"
              style="height: calc(100vh - 140px); background-color: #F2F2F2;">
              <!-- CAMERA_MOVIE_TYPE.CAMERAの履歴の場合 -->
              <camera-play-back />
            </div>
          </div>

          <!-- 下部エリア： 時系列サムネイル: HLS_CAMERAのhistoryのみ -->
          <camera-history-thumbnail-grid
            v-bind="{ camera, mode, thumbnailUrlList, carouselItemWidth, selectedHour, setTargetHour, thumbJstHHMM, targetHour, noImageUrl, jumpCarouselPosition, setCarouselPosition, carouselScrollRate, AISEA_COLOR }"
            @scroll="carouselScrollRate = $event" />

        </div>
      </template>
    </vx-card>

    <div id="custom-breadcrumb-title" />

    <div v-for="(data, index) of thumbnailUrlList" :key="index">
      <div style="display: none; position: absolute; bottom: 0; right: 0; z-index: 99999; width: 140px; height: 120px;">
        <video-player v-if="mode === 'history'" id="hidden-video-player"
          class="media-video-player card-video vjs-big-play-centered" :ref="`hiddenVideoPlayer${index}`"
          :options="hiddenPlayerOptions" :style="{ 'min-width': '100%', 'min-height': '100%', }" />
      </div>
    </div>
    <onvif-confirmation-dialog
      :visible.sync="showOnvifDialog"
      @accept="handleOnvifAccept"
      @cancel="handleOnvifCancel"
    />
  </div>
</template>

<script>
// モジュール
import Vue from "vue";
import { videoPlayer } from "vue-videojs7";
import "vue-slider-component/theme/antd.css";
import videojs from "video.js";
import moment from "moment-timezone";
import _ from "lodash";
import DatepickerMixin from "@/mixins/DatepickerMixin";

// Mixin
import { CameraMixin, DeviceInfoMixin, ColorMixin } from "@/mixins";

// コンポーネント
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import { storageWebCam, storageMediamtx } from "@/firebase/firebaseConfig";
import { createNamespacedHelpers } from "vuex";
const { mapState, mapActions } = createNamespacedHelpers(
  "enterpriseCameraList"
);

// components
import "flatpickr/dist/flatpickr.css";
import { Japanese } from "flatpickr/dist/l10n/ja.js";

import {
  customizeBreadcrumb,
  sleep,
  zoomList,
} from "./utils/cameraHelper";

import {
  hykeCamTimeout,
  hykeCamResize,
  hykeCamLoadStart,
  hykeCamLoadedData,
  hykeCamScreen
} from "./utils/hykeCameraHelper";

import {
  onvifInitControl,
  onvifPtzByStep,
  onvifPtzByScroll,
  onvifStopScroll,
  onvifGotoHomePosition,
  onvifSetHomePosition,
  onvifSetHomePositionConfirm,
  onvifReboot,
  onvifUpdateResolution
} from "./utils/onvifHelper";

import {
  isExistsThumbnail,
  generateThumbnailPatchUp,
  convertGcsPath,
  refreshThumbnailUrlList
} from "./utils/historyThumbnailHelper";

import {
  playVideo,
  playVideoHls,
  playVideoEcomott,
  showPlayButton,
  hidePlayButton,
  errorHandler,
  hasValidMovie
} from "./utils/videoPlayerHelper";

export default {
  components: {
    videoPlayer,
    CameraPlayBack: () => import("./components/CameraPlayBack"),
    CameraHeaderView: () => import("./components/CameraHeaderView"),
    CameraMainView: () => import("./components/CameraMainView"),
    OnvifCameraControlView: () => import("./components/OnvifCameraControlView"),
    CameraHistoryThumbnailGrid: () => import("./components/CameraHistoryThumbnailGrid"),
    OnvifConfirmationDialog: () => import("./components/OnvifConfirmationDialog"),
  },
  mixins: [CameraMixin, DatepickerMixin, DeviceInfoMixin, ColorMixin],
  props: {
    mode: { type: String, required: true },
  },
  data() {
    return {
      camera: null,
      cameraReady: false,
      angleHorizontal: 0,
      angleVertical: 0,
      sliderFormatter: val => `${val}°`,
      marksHorizontal: val => val % 90 === 0 ? ({
        label: `${val}°`,
      }) : false,
      marksVertical: val => val % 10 === 0 ? ({
        label: `${val}°`,
      }) : false,
      thumbnailUrlList: [],
      thumbnails: [],
      targetDateYmd: moment().local().startOf('date').toDate(),
      targetDate: moment().startOf('hour').toDate(),
      targetZoom: 5,
      onvifEnable: false,
      onvifActive: false,
      cameraZoomMin: 1,
      cameraZoomMax: 1,
      canRealtime: true,
      selectedHour: -1,
      initialized: false,
      hasVideoError: false,
      retryText: "",
      cameraMsg: "",
      hlsSrc: null,
      webrtcUrl: null,
      cameraMode: {},
      dateList: {},
      errorDisplay: null,
      onvifBaseParameter: null,
      onvifMovingStopTimer: null,
      popupActive: false,
      pickerConfig: {
        playDate: null,
        playStartTime: null,
        enableDateList: null
      },
      cameraModel: null,
      cameraSmallMove: null,
      cameraLargeMove: null,
      cameraZoomMove: null,
      seekCurrent: 0,
      intervalId: -1,
      videoPlayerWidthByHeight: '80%',
      zoomSize: '100%',
      trackIndexForSafie: 0,
      noImageUrl: require("@/assets/images/icons/cameras/video_off.svg"),
      carouselScrollRate: 0,
      cameraUpDownStep: 15,
      cameraRightLeftStep: 15,
      onvifCurrentAngleX: 0,
      onvifCurrentAngleY: 0,
      configDayPicker: {
        dateFormat: "Y/m/d",
        time_24hr: true,
        defaultDate: moment().startOf('hour').toDate(), // new Date(),
        enable: [],
        locale: Japanese
      },
      lastMovieFileName: null,
      loadingFlg: false,
      fps: 1,
      carouselItemWidth: '160px',
      isProcessing: false,
      processingMessage: '',
      volume: 0,
      hykeCamTimer: null,
      hykeCamUrl: null,
      hykeCamHeight: "100%",
      hykeCamLoaderShow: false,
      hykeCamLoaderHeight: 100,
      hykeCamLoaderWidth: 100,
      hykeCamLoaderTop: 50,
      hykeCamLoaderLeft: 50,
      isHistoryPlayback: false,
      healthCheckTimer: null,
      remoteItProxyUrl: null,
      urlForOnvif: null,
      showOnvifDialog: false,
    };
  },
  computed: {
    ...mapState({
      cameras: state => state.cameras
    }),
    player() {
      if (!this.$refs.cameraMainView || !this.$refs.cameraMainView.$refs.videoPlayer) return null;
      const liveCam = this.$refs.cameraMainView.$refs.videoPlayer;
      return liveCam.$refs && liveCam.$refs.videoPlayer ? liveCam.$refs.videoPlayer.player : null;
    },
    showTimeRangePicker() {
      return this.camera.movieType !== this.CAMERA_MOVIE_TYPE.RADAR
        && this.camera.movieType !== this.CAMERA_MOVIE_TYPE.HLS_RADAR;
    },
    mainPlayerOptions() {
      let playerOption = JSON.parse(JSON.stringify(this.getPlayerOption(this.camera.movieType)));
      playerOption.controls = false;
      playerOption.autoplay = true;
      if (this.hlsSrc) {
        playerOption.sources = [{ src: this.hlsSrc, type: "application/x-mpegURL" }];
      }
      return playerOption;
    },
    hiddenPlayerOptions() {
      let playerOption = JSON.parse(JSON.stringify(this.getPlayerOption(this.camera.movieType)));
      playerOption.controls = false;
      playerOption.autoplay = false;
      playerOption.sources = [];
      return playerOption;
    },
    targetDateYmdSlash() {
      return this.targetDateYmd && this.targetDate ? moment(this.targetDate).format('YYYY/MM/DD') : '----/--/--';
    },
    targetHour() {
      return this.targetDateYmd && this.targetDate ? moment(this.targetDate).format('HH') + ':00' : '--:--';
    },
    playStartMomentValueOf() {
      return moment(this.targetDate).utc().unix().valueOf();
    },
    currentMomentValueOf() {
      return this.playStartMomentValueOf + this.seekCurrent;
    },
    zoomList,
    canPrevDate() {
      const prevDate = moment(this.targetDate).local().startOf('day').add(-1, 'days');
      return this.configDayPicker.enable.length > 0
        && prevDate.format('YYYY-MM-DD') >= this.configDayPicker.enable[0];
    },
    canNextDate() {
      const nextDate = moment(this.targetDate).local().startOf('day').add(1, 'days');
      return nextDate.valueOf() < moment().valueOf()
        && this.configDayPicker.enable.length > 0
        && nextDate.format('YYYY-MM-DD') <= this.configDayPicker.enable[this.configDayPicker.enable.length - 1];
    },
    videoPlayerAreaHeightOffset() {
      if (this.mode === 'history' && this.camera && [this.CAMERA_MOVIE_TYPE.HLS_CAMERA, this.CAMERA_MOVIE_TYPE.SAFIE, this.CAMERA_MOVIE_TYPE.MEDIAMTX].includes(this.camera.movieType)) {
        return '320px'; // '310px';
      }
      return '140px';
    },
    onvifAreaSize() {
      return this.camera && this.onvifEnable && this.onvifActive
        // ? {x: '100px', y: '92px'}
        ? { x: '220px', y: ' 0px' }
        : { x: '0px', y: '0px' };
    },
    thumbJstHHMM: () => (thumbnailUrl) => {
      const thumbnailUrlParts = thumbnailUrl.split('/');
      if (thumbnailUrlParts.length >= 3 && !isNaN(thumbnailUrlParts[thumbnailUrlParts.length - 2])) {
        const jstHH = (Number(thumbnailUrlParts[thumbnailUrlParts.length - 2]) + 9) % 24;
        return ('00' + jstHH).slice(-2) + ':00';
      }
      return '';
    },
    debugTargetDate() {
      return moment(this.targetDate).format("HH:mm:ss")
        + ', ' + moment(this.targetDate).utc().format("YYYYMMDD/HH")
        + ', ' + moment(this.targetDate).toISOString()
        + ', ' + this.targetDate;
    },
    isHykeCam() {
      return !this.camera ? false : this.camera.movieType === this.CAMERA_MOVIE_TYPE.HYKECAM;
    },
    isEcomottCam() {
      return this.camera.movieType === this.CAMERA_MOVIE_TYPE.ECOMOTT
    },
    isWebRTC() {
      return this.camera.movieType === this.CAMERA_MOVIE_TYPE.MEDIAMTX;
    },
    hykeCamHeightPx() {
      return this.hykeCamHeight + "px";
    },
    hykeCamLoaderHeightPx() {
      return this.hykeCamLoaderHeight + "px";
    },
    hykeCamLoaderWidthPx() {
      return this.hykeCamLoaderWidth + "px";
    },
    hykeCamLoaderTopPx() {
      return this.hykeCamLoaderTop + "px";
    },
    hykeCamLoaderLeftPx() {
      return this.hykeCamLoaderLeft + "px";
    },
    volumeIcon() {
      if (this.volume <= 0.01) return "VolumeXIcon";
      if (this.volume >= 0.01 && this.volume <= 0.5) return "Volume1Icon";
      return "Volume2Icon";
    },
    verticalNavMenuWidth() {
      return this.$store.state.verticalNavMenuWidth;
    },
  },
  watch: {
    targetDate: {
      handler: _.debounce(function () {
        this.trackIndexForSafie = 0;
        this.connect();
      }, 800),
    },
    targetZoom() {
      if (this.mode === 'history') {
        // 過去データのZoom補正は、単純な画像引き伸ばし
        const zoomRateUnit = 1;
        this.zoomSize = `${this.targetZoom * zoomRateUnit * 100}%`;
      }
    },
    targetDateYmd() {
      if (this.mode === 'history') {
        this.loadingFlg = true;
        this.$vs.loading({container: "#camera-center-area",});

        const selectedDay = moment(this.targetDateYmd).startOf('day');
        const today = moment().startOf('day');

        if (selectedDay.isSame(today, 'day')) {
          this.targetDate = moment();
          this.isHistoryPlayback = false;
          this.selectedHour = -1;
        } else {
          this.targetDate = selectedDay.clone().add(12, 'hour').toDate();
          this.isHistoryPlayback = true;
          this.selectedHour = 12;
        }

        this.refreshThumbnailUrlList(this);
        setTimeout(() => {
          this.setCarouselPosition(this.selectedHour);
        }, 2000);
      }
    },
    async onvifActive(newVal) {
      if (this.camera && this.onvifEnable) {
        if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.MEDIAMTX && newVal) {
          this._onvifConnecting = true;
          await this.connectToWebRtc();
          if (!this.onvifActive || !this._onvifConnecting) {
            this._onvifConnecting = false;
            return;
          }
          this._onvifConnecting = false;
          this._onvifTimeout = setTimeout(() => {
            if (this.onvifActive) {
              this.showOnvifDialog = true;
              
              this._onvifPopupTimeout = setTimeout(() => {
                if (this.showOnvifDialog) {
                  this.showOnvifDialog = false;
                  this.clearOnvifTimers();
                  this.onvifActive = false;
                }
              }, 60 * 1000);
            }
          }, 5 * 60 * 1000);
          this.startHealthCheck();
        } else {
          this._onvifConnecting = false;
          this.clearOnvifTimers();
          this.showOnvifDialog = false;
          this.connect();
          this.stopHealthCheck();
        }
        this.$nextTick(() => {
          this.recalcPlayerSize();
          if (this.$refs.playBackControls && this.player) {
            if (!this.$refs.playBackControls.isPlay) {
              this.player.play();
              this.$refs.playBackControls.isPlay = true;
            }
          }
        });
      }
    },
    onChangeWindowSize() {
      this.recalcPlayerSize();
      this.setCarouselItemWidth();
    },
    verticalNavMenuWidth() {
      setTimeout(() => {
        this.recalcPlayerSize();
        this.setCarouselItemWidth();
      }, 1200);
    },
  },
  async mounted() {

    let routerViewElement = document.getElementsByClassName("router-view");
    routerViewElement[0].style.padding = "2.2rem";

    await this.fetchCameras();
    this.camera = await this.cameras.find(x => x.id === this.$route.params.cameraId);
    if (this.mode === 'history' && this.camera.movieType !== this.CAMERA_MOVIE_TYPE.CAMERA) {
      await this.extractEnableDates(this.camera);
    }
    this.cameraReady = true;

    if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.ECOMOTT) {
      const username = this.camera.onvifUserName
      const password = this.camera.onvifPassword

      if (videojs && videojs.Vhs && videojs.Vhs.xhr) {
        videojs.Vhs.xhr.beforeRequest = function (options) {
          options = options || {};
          options.headers = options.headers || {};
          options.headers['Authorization'] =
            'Basic ' + btoa(`${username}:${password}`);
          return options;
        };
      }
    }

    // 履歴指定の場合、date-pickerをアクティブに
    if (this.mode === 'history' && this.camera.movieType !== this.CAMERA_MOVIE_TYPE.CAMERA) {
      this.$nextTick(() => {
        this.$el.querySelector('input.flatpickr-input').click();
        this.$el.querySelector('input.flatpickr-input').focus();
      });
    }

    // Playerの currentTimeを定期的に監視
    this.intervalId = setInterval(this.syncSeekPosition, 5000);

    this.$nextTick(async () => {
      this.recalcPlayerSize();
      this.setCarouselItemWidth();

      // パンくずリストのカスタマイズ
      this.customizeBreadcrumb();
    });

    // ハイクカム用の初期化
    if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.HYKECAM) {
      window.addEventListener("resize", this.hykeCamResize, false);
      this.$refs.hykeCamVideo.addEventListener("loadstart", this.hykeCamLoadStart, false);
      this.$refs.hykeCamVideo.addEventListener("loadeddata", this.hykeCamLoadedData, false);
      this.hykeCamTimeout();
      this.hykeCamTimer = setInterval(this.hykeCamTimeout, 5000);
    }

    if (this.mode === 'history') {
      this.isHistoryPlayback = false;
      this.selectedHour = -1;
      await this.connect();
    }
  },
  beforeDestroy() {
    clearInterval(this.intervalId);

    this.stopHealthCheck();
    this.clearOnvifTimers();
    this._onvifConnecting = false;
    // ハイクカム用のクリア処理
    if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.HYKECAM) {
      clearInterval(this.hykeCamTimer);
    }
  },
  methods: {
    ...mapActions(["hykeCamGetMedia", "fetchCameras"]),

    startHealthCheck() {
      if (this.healthCheckTimer) return;
      
      if (!this.remoteItProxyUrl) {
        Vue.$log.warn('RemoteIt proxy URL not available for health check');
        return;
      }

      Vue.$log.debug('Starting health check for RemoteIt proxy:', this.remoteItProxyUrl);

      this.healthCheckTimer = setInterval(async () => {
        try {
          const res = await fetch(`${this.remoteItProxyUrl}/healthz`, { 
            method: 'GET',
          });
          if (res.ok) {
            Vue.$log.debug('Health check success');
          } else {
            Vue.$log.warn('Health check failed:', res.status);
          }
        } catch (e) {
          Vue.$log.error('Health check error:', e);
        }
      }, 60 * 1000); // 1 minute
    },

    stopHealthCheck() {
      if (this.healthCheckTimer) {
        Vue.$log.debug('Stopping health check');
        clearInterval(this.healthCheckTimer);
        this.healthCheckTimer = null;
      }
    },

    // camera-connectメソッド群
    async connectToCamera() {
      try {
        let res = await AiseaApiFactory.get("enterpriseCamera").connect(
          this.camera.id
        );
        return res.data.url;
      } catch (e) {
        return null;
      }
    },
    async connectToWebRtc() {
      try {
        const proxyUrl = await this.getRemoteItProxyUrl();
        const hash = this.camera.hash;
        if (proxyUrl.url) {
          this.webrtcUrl = `${proxyUrl.url}/${hash}/whep`;
          this.remoteItProxyUrl = this.urlForOnvif;
        } else {
          this.$vs.notify({
            color: 'danger',
            title: proxyUrl.message,
          });
        }

        if (this.urlForOnvif) {
          this.onvifInitControl(this.urlForOnvif);
        }

        return this.webrtcUrl;
      } catch (e) {
        this.webrtcUrl = null;
        this.remoteItProxyUrl = null;
        this.cameraMsg = e.message;
        this.hasVideoError = true;
        return null;
      }
    },
    async getRemoteItProxyUrl() {
      try {
        let res = await AiseaApiFactory.get("enterpriseCamera").webRtc(
          this.camera.id
        );
        return res.data;
      } catch (e) {
        return null;
      }
    },
    async connectToHlsCamera() {
      try {
        let res = await AiseaApiFactory.get("enterpriseCamera").connectHLS(
          this.camera.id
        );
        if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.MEDIAMTX && !res.status) {
          let msg = "";
          if (res.code === 404) {
            if (res.data.message.includes("no stream is available on path")) {
              msg = "カメラが起動していないか、映像が取得できません";
            } else {
              msg = "通信がタイムアウトしました。ネットワーク状態を確認してください";
            }
          } else if (res.code === 502) {
            msg = "通信がタイムアウトしました。ネットワーク状態を確認してください";
          } else if (res.code === 401) {
            msg = "映像の認証に失敗しました。ログイン情報をご確認ください";
          } else if (res.code === 400) {
            msg = "同時接続数の上限に達しました。別の端末で視聴中の可能性あります。";
          } else {
            msg = res.message;
          }
          this.$vs.notify({
            color: 'danger',
            title: msg,
          });
        } else {
          return res.data.url;
        }
      } catch (e) {
        return null;
      }
    },
    async connect() {
      if (this.mode === 'history' && !this.isHistoryPlayback) {
        let url = await this.connectToHlsCamera();
        this.hlsSrc = url;
        if (url) {
          this.playVideoHls(url);
          if (this.$refs.playBackControls) {
            this.$refs.playBackControls.isPlay = true;
          }
        } else {
          this.errorHandler();
        }
        this.loadingFlg = false;
        this.$vs.loading.close("#camera-center-area > .con-vs-loading");
      } else {
        if ( this.mode === 'history' && this.camera.movieType === this.CAMERA_MOVIE_TYPE.CAMERA ) {
          this.$vs.loading.close("#camera-center-area > .con-vs-loading");
          this.loadingFlg = false;
          return;
        }
        if (!this.loadingFlg) {
          this.loadingFlg = true;
          this.$vs.loading({ container: "#camera-center-area", });
          setTimeout(() => {
            this.$vs.loading.close("#camera-center-area > .con-vs-loading");
            this.loadingFlg = false;
          }, 60 * 1000);
        }
        try {

          this.hasVideoError = false;
          this.cameraMsg = "";
          let url

          if ((this.camera.movieType === this.CAMERA_MOVIE_TYPE.HLS_CAMERA
            || this.camera.movieType === this.CAMERA_MOVIE_TYPE.SAFIE
            || this.camera.movieType === this.CAMERA_MOVIE_TYPE.HLS_RADAR
            || this.camera.movieType === this.CAMERA_MOVIE_TYPE.MEDIAMTX)
            && !(this.initialized && this.camera && this.cameraReady && this.onvifEnable && this.onvifActive)
          ) {
            url = await this.connectToHlsCamera();
            this.hlsSrc = url;
            if (url) {

              if (this.mode !== 'history' && this.camera.onvifUserName) {
                this.urlForOnvif = await this.connectToCamera();
                if (this.urlForOnvif) {
                  // onvifカメラではないと事前に検知できた場合は、そもそも初期化しない。
                  this.onvifInitControl(this.urlForOnvif);
                }
              }
  
              this.canRealtime = await this.hasValidMovie(url, this.camera.movieType);
  
              const isPastHour = moment(this.targetDate).startOf('hour').valueOf() < moment().startOf('hour').valueOf() 
                                || ( this.mode === 'history' && this.isHistoryPlayback && moment(this.targetDate).startOf('hour').valueOf() === moment().startOf('hour').valueOf()  ) ;
              let trackUrl = null;
              if ( isPastHour ) {
                const ymdhUtc = moment(this.targetDate).utc().format("YYYYMMDD/HH") ;
                url = url.replace(/HLS.m3u8/i, `${ymdhUtc}/OnDemand.m3u8`);
  
                if ( this.camera.movieType === this.CAMERA_MOVIE_TYPE.SAFIE ) {
                  // safieの履歴表示は、さらに10分単位に分割
                  trackUrl = await this.convertOnDemandUrlForSafie(url, ymdhUtc);
                }
              }

              if (this.player && this.mode !== 'history') {
                // TODO: 検証
                this.player.loop(true);
              }


              this.playVideoHls(url, trackUrl);

              if (isPastHour && this.player && this.mode === 'history') {
                let seekCurrent = 0;
                if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.SAFIE) {
                  seekCurrent = this.seekCurrent;
                }
                this.seekCurrent = seekCurrent;
                this.player.currentTime(0);

                this.player.play();
                if (this.$refs.playBackControls) {
                  this.$refs.playBackControls.isPlay = true;
                  this.$refs.playBackControls.kickCreateThumbnails(url);
                }
                setTimeout(() => {
                  this.player.playbackRate(this.fps);
                }, 1800);
              } else if (this.player && this.mode !== 'history') {
                if (this.$refs.playBackControls) {
                  this.$refs.playBackControls.isPlay = true;
                }
              }


            } else {
              this.errorHandler();
            }
          } else if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.ECOMOTT) {
            const url = `${this.camera.device_address}/${this.camera.hash}/index.m3u8`;
            this.hlsSrc = url;
            if (url) {
              this.playVideoEcomott(url);
            } else {
              this.errorHandler();
            }
          } else {
            url = await this.connectToCamera();
            this.hlsSrc = url + "/webcam/hls/stream.m3u8";
            if (url) {
              this.playVideo(url);
              if (this.$refs.playBackControls) {
                this.$refs.playBackControls.isPlay = true;
              }
              if (this.camera.onvifUserName) {
                // onvifカメラではないと事前に検知した場合は、そもそも初期化しない。
                this.onvifInitControl(url);
              }
            } else {
              this.errorHandler();
            }
          }
        } catch (e) {
          Vue.$log.debug("problem at connect()", e);
        }

        setTimeout(() => {
          this.$vs.loading.close("#camera-center-area > .con-vs-loading");
          this.loadingFlg = false;
        }, 60 * 1000);
      }

      setTimeout(() => {
        this.$vs.loading.close("#camera-center-area > .con-vs-loading");
        this.loadingFlg = false;
      }, 1000);

      if (this.mode === 'history') {
        this.targetZoom = 1;
        this.cameraZoomMin = 1;
        this.cameraZoomMax = 5;
      }
    },
    playVideo(bassUrl) { playVideo(this, bassUrl); },
    playVideoHls(baseUrl, trackUrl) { playVideoHls(this, baseUrl, trackUrl); },
    playVideoEcomott(baseUrl) { playVideoEcomott(this, baseUrl); },
    errorHandler() { errorHandler(this); },
    async convertOnDemandUrlForSafie(url, ymdhUtc) {
      // safieの履歴表示は、m3u8ファイルを10分単位に再構成
      const m3u8OrigText = await fetch(url, { mode: 'cors' }).then(res => res.text());
      const m3u8OrigList = m3u8OrigText.split("\n");
      const m3u8List = [];
      const filterRegExp = new RegExp(ymdhUtc.replace(/\//, '-') + this.trackIndexForSafie, 'i');
      let prevLine = "";
      for (const line of m3u8OrigList) {
        if (line.match(/^#/) && !line.match(/^#EXTINF:/)) {
          m3u8List.push(line);
        } else if (line.match(/\.ts$/)) {
          if (line.match(filterRegExp)) {
            m3u8List.push(prevLine);
            m3u8List.push(line);
          }
        }
        prevLine = line;
      }

      const m3u8Text = m3u8List.join("\n")
      url = URL.createObjectURL(new Blob([m3u8Text], { type: 'text/plain' }));
      return url;
    },

    async hasValidMovie(url, movieType) { return await hasValidMovie(this, url, movieType); },


    // video-player用メソッド群
    async onPlayerReadied() {
      if (!this.initialized && this.cameraReady === true) {
        await this.connect();
        this.initialized = true;
        if (this.mode === 'history'
          && [this.CAMERA_MOVIE_TYPE.HLS_CAMERA, this.CAMERA_MOVIE_TYPE.SAFIE, this.CAMERA_MOVIE_TYPE.MEDIAMTX].includes(this.camera.movieType) && this.trackIndexForSafie === 0) {
          this.refreshThumbnailUrlList(this);
        }
      }
    },
    onPlayerPlay() {
      this.hidePlayButton();
      this.player.error(null);
      if (this.$refs.playBackControls) {
        this.$refs.playBackControls.isPlay = true;
      }
    },
    onPlayerPause() {
      this.showPlayButton();
      if (this.$refs.playBackControls) {
        this.$refs.playBackControls.isPlay = false;
      }
    },
    onPlayerError(player) {
      const errorCode = player && player.error() && player.error().code;
      if (errorCode !== 3) {
        this.cameraMsg = "";
        this.retryText = "もう一度接続";
        this.hidePlayButton();
        this.hasVideoError = true;
        if (this.$refs.playBackControls) {
          this.$refs.playBackControls.setPause();
        }
      } else {
        if ( this.mode === 'realtime' || (this.mode === 'history' && !this.isHistoryPlayback)) {
          // 3: MEDIA_ERR_DECODE でエラーになった場合は 再接続をコール
          player.error(null);
          this.connect().then(() => {
            if (this.player) {
              this.player.play();
            }
          });
        } else if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.SAFIE) {
          // Safieの履歴表示でエラーになった時、トラックの境界線に当たった可能性があるので、seekCurrent時間を少し進めて再コール
          player.error(null);
          const seekCurrent = this.seekCurrent + 30;
          const playerCurrentTime = seekCurrent % 600;
          const trackIndexForSafie = Math.floor(seekCurrent / 600);
          this.trackIndexForSafie = trackIndexForSafie;
          this.seekCurrent = seekCurrent;
          this.connect().then(() => {
            if (this.player) {
              this.player.play();
              for (let i = 0; i < (5000 / 100 / 2); i++) {
                setTimeout(() => {
                  this.player.currentTime(playerCurrentTime);
                  this.seekCurrent = seekCurrent;
                }, i * 100);
              }
            }
          });
        }
      }
    },
    onPlayerEnded() {
      if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.SAFIE && this.mode === 'history') {
        if (this.trackIndexForSafie < 6) {
          // SafieでPlayerEndedを検知した時、次のトラックをコール
          this.trackIndexForSafie += 1;
          this.connect();
        }
        return;
      }

      if (this.existForward) {
        this.onSkipForward();
      }

      this.player.play();


    },
    onSkipForward() {
      this.playNextOnDemand(3600);
      this.existBack = true;

    },
    playNextOnDemand(offset) {
      let utcStartTime = moment.unix(this.start + offset);
      this.start = moment(utcStartTime).unix();
      let utcEndTime = moment.unix(this.end + offset);
      this.end = moment(utcEndTime).unix();
      let path = this.baseUrl.split("/");
      path.pop();
      path.pop();
      this.baseUrl = path.join("/") + moment(utcStartTime).utc().format("/YYYYMMDD/HH");
      this.srcUrl = this.baseUrl + "/OnDemand.m3u8";
      this.posterUrl = this.baseUrl + "/thumbnail.png";
      let currentTime = this.currentTime;
      this.playOnDemand();
      this.checkNextOnDemand(offset);
      if (offset > 0) {
        this.backTime = currentTime;
      } else {
        this.forwardTime = currentTime;
      }
    },
    playOnDemand() {
      if (this.player && this.srcUrl) {
        this.player.src({
          withCredentials: false,
          type: "application/x-mpegurl",
          src: this.srcUrl,
        })
        this.player.poster(this.posterUrl);
        // m3u8はHLSの機能なので、1Hくらい遡っておく
        this.player.currentTime(-3650);
        this.showPlayButton();
        this.popupActive = true;
      }
    },
    async checkNextOnDemand(offset) {
      let utcStartTime = moment.unix(this.start + offset);
      let utcEndTime = moment.unix(this.end + offset);
      let path = this.baseUrl.split("/");
      path.pop();
      path.pop();
      let baseUrl = path.join("/") + moment(utcStartTime).utc().format("/YYYYMMDD/HH");
      let res = await this.existOnDemand(baseUrl + "/OnDemand.m3u8");
      let startLocal = utcStartTime
        .tz("Asia/Tokyo")
        .format("YYYY年MM月DD日 HH:mm");
      let endLocal = utcEndTime
        .tz("Asia/Tokyo")
        .format("HH:mm");
      if (!res) {
        if (offset > 0) {
          this.existForward = false;
          this.forwardTime = `${startLocal} - ${endLocal}`;
        } else {
          this.existBack = false;
          this.backTime = `${startLocal} - ${endLocal}`;
        }
      } else {
        if (offset > 0) {
          this.existForward = true;
          this.forwardTime = `${startLocal} - ${endLocal}`;
        } else {
          this.existBack = true;
          this.backTime = `${startLocal} - ${endLocal}`;
        }
      }
    },
    async existOnDemand(src) {
      try {
        await fetch(src, { mode: 'cors' });
      } catch (err) {
        return false;
      }
      return true;
    },
    showPlayButton() { showPlayButton(this); },
    hidePlayButton() { hidePlayButton(this); },

    // コントロールバーエリアからのイベントドリブンメソッド群
    onPlay() {
      if (this.player) {
        if (this.player.paused()) {
          this.player.play();
        } else {
          this.player.pause();
        }
        if (this.$refs.playBackControls) {
          this.$refs.playBackControls.isPlay = !this.player.paused();
        }
      }
    },
    onChangeVolume(volume) {
      this.volume = volume;
      this.player.volume(volume);
      if (volume === 0) {
        this.player.muted(true);
      } else if (volume > 0 && volume <= 1) {
        this.player.muted(false);
      }
    },
    setFPS(fps) {
      this.player.pause();
      try {
        this.player.playbackRate(fps);
        this.fps = fps;
      } catch (e) {
        this.fps = 1;
        Vue.$log.warn("playbackRate error", e);
      }
      this.$nextTick(() => {
        this.player.play();
      });
    },
    onChangeSeek(currentTime) {
      const seekTime = currentTime;
      this.onChangeSeekCurrent(seekTime);
    },
    onChangeSeekCurrent(val) {
      if (this.player) {
        this.setCurrentSecond(this, val);
      }
    },
    setCurrentSecond: _.debounce((self, val) => {
      if (self.camera.movieType !== self.CAMERA_MOVIE_TYPE.SAFIE) {
        self.seekCurrent = Math.ceil(val / 5) * 5;
        self.player.currentTime(self.seekCurrent);
      } else {
        const seekCurrent = Math.ceil(val / 5) * 5;
        const playerCurrentTime = seekCurrent % 600;
        const trackIndexForSafie = Math.floor(seekCurrent / 600);
        if (self.trackIndexForSafie !== trackIndexForSafie) {
          // Safieで seek時間が別のブロックを指した時、connect()をコールする。
          self.trackIndexForSafie = trackIndexForSafie;
          self.seekCurrent = seekCurrent;
          self.connect().then(() => {
            self.player.currentTime(playerCurrentTime);
            self.seekCurrent = seekCurrent;
            for (let i = 0; i < 5000 / 100 / 2; i++) {
              setTimeout(() => {
                self.player.currentTime(playerCurrentTime);
                self.seekCurrent = seekCurrent;
              }, i * 100);
            }
          });
        } else {
          self.player.currentTime(playerCurrentTime);
          self.seekCurrent = seekCurrent;
        }
      }
      setTimeout(() => {
        self.syncSeekPosition();
      }, 1000);
    }, 400),
    setFullscreen(isFullscreen) {
      this.isFullscreen = isFullscreen;
      if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.MEDIAMTX && this.$refs.cameraMainView) {
        this.$refs.cameraMainView.$el.querySelector('video').requestFullscreen();
      } else if (this.player && isFullscreen) {
        this.player.requestFullscreen();
      } else {
        this.$refs.playBackControls.setStyleToSmall();
      }
    },
    onFullscreenChange() {
      // Escボタンによるフルスクリーン解除をフック
      if (!document.fullscreenElement) {
        this.$refs.playBackControls.setStyleToSmall();
      }
    },

    // カレンダーの有効日付（映像データが存在する）のリスト抽出
    async extractEnableDates(camera) {
      let enableDateList = [];
      let storage = camera.movieType === this.CAMERA_MOVIE_TYPE.MEDIAMTX
        ? storageMediamtx
        : storageWebCam;
      storage
        .child(camera.hash)
        .listAll()
        .then(response => {
          if (response.prefixes.length == 0) {
            this.$vs.notify({
              color: "danger",
              title: "指定されたカメラの録画データがみつかりませんでした"
            });
          } else {
            _.forEach(response.prefixes.filter(prefix => prefix.name.match(/^[0-9]{8}/)), function (item) {
              enableDateList.push(
                moment(item.name)
                  .utc()
                  .tz("Asia/Tokyo")
                  .format("YYYY-MM-DD")
              );
            });

            this.configDayPicker.enable = enableDateList;
            this.targetDateYmd = moment(enableDateList[enableDateList.length - 1], ['YYYY-MM-DD']).toDate();
          }
        })
        .catch(() => {
          this.$vs.notify({
            color: "danger",
            title: "カメラの録画データ取得に失敗しました"
          });
        });

    },
    // カレンダーを１日移動
    incrementTargetDate(val) {
      this.loadingFlg = true;
      this.$vs.loading({ container: "#camera-center-area", });

      let candidateMoment = null;
      for (let i = 1; i < 365 * 3; i++) {
        // 無効日を避けて次の日付を決める。
        candidateMoment = moment(this.targetDate).add(val * i, 'day').startOf('day');
        if (candidateMoment.valueOf() > moment().valueOf()) {
          // 未来日には行けない
          this.loadingFlg = false;
          this.$nextTick(() => {
            this.$vs.loading.close("#camera-center-area > .con-vs-loading");
          });
          return;
        } else if (!this.configDayPicker.enable.map(x => moment(x, ['YYYY-MM-DD']).toDate().getTime()).includes(candidateMoment.toDate().getTime())) {
          // 無効日の場合は、翌日or前日にずらして再判定
          continue;
        } else {
          // 上記以外なら、有効日として日付変更を開始
          this.targetDateYmd = candidateMoment.toDate();
          break;
        }
      }
      this.loadingFlg = false;
      this.isHistoryPlayback = true;
      this.$nextTick(() => {
        this.$vs.loading.close("#camera-center-area > .con-vs-loading");
      });
      return;
    },

    // カメラエリアの表示サイズ最適化
    recalcPlayerSize() {
      const videoPlayerAreaElement = document.getElementById('video-player-area');
      if (videoPlayerAreaElement && this.player) {
        const rect = this.player.el_.getBoundingClientRect();
        const adjustedWidth = (videoPlayerAreaElement.getBoundingClientRect().height * rect.width / rect.height);
        if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.SAFIE && this.mode === 'history') {
          this.videoPlayerWidthByHeight = (adjustedWidth - 180) + 'px';
        } else {
          this.videoPlayerWidthByHeight = adjustedWidth + 'px';
        }
      }
    },

    // カメラ表示エリアをクリックした時
    onVideoClick() {
      this.$refs.playBackControls.onPlay();
    },

    // Playerの currentTimeを定期的に監視
    syncSeekPosition() {
      if (this.player && this.initialized && !this.thumbnailProcessingFlg) {
        if (this.camera.movieType !== this.CAMERA_MOVIE_TYPE.SAFIE) {
          this.seekCurrent = this.player.currentTime();
        } else {
          // Safieの場合、ローディング中は seekPositionのsyncをやめる。
          if (this.loadingFlg === false) {
            this.seekCurrent = this.player.currentTime() + this.trackIndexForSafie * 600;
          }
        }
      }
    },

    // 一覧へ戻るをクリックした時
    toListClicked() {
      this.$router.push({
        name: "enterprise-management-camera-list",
      });
    },

    // live <--> 履歴 切り替えボタンをクリックした時
    changeMode() {
      const nextPageName = (this.mode === 'history')
        ? "enterprise-management-camera-view-realtime"
        : "enterprise-management-camera-view-history";
      const routerPush = {
        name: nextPageName,
        params: { cameraId: this.camera.id },
      };
      this.$router.push(routerPush);
    },

    // 時系列サムネイルリストが横スクロールされた時
    onCarouselScroll() {
      const carouselWrapEl = this.$el ? this.$el.querySelector(".carousel-wrap") : null;
      this.carouselScrollRate = carouselWrapEl ? carouselWrapEl.scrollLeft / (carouselWrapEl.scrollWidth - carouselWrapEl.getBoundingClientRect().width) : null;
    },
    // 時系列サムネイルリストをブロック単位でジャンプさせるとき
    jumpCarouselPosition(val) {
      const el = this.$el.querySelector('div.carousel-wrap');
      const scrollUnitSize = el.getBoundingClientRect().width * 0.8 * (val > 0 ? +1 : -1);
      el.scrollTo(el.scrollLeft + scrollUnitSize, 0);
    },
    // 時系列サムネイルリストの横スクロールを時刻に合わせてセットするとき
    setCarouselPosition(hour) {
      const el = this.$el.querySelector('div.carousel-wrap');
      if (el) {
        const scrollPosition = el.getBoundingClientRect().width * (hour - 4) / 8;
        el.scrollTo(scrollPosition, 0);
      }
    },

    // 時系列サムネイルをクリック
    setTargetHour(thumbnailUrl, hour) {
      if (thumbnailUrl === this.noImageUrl) {
        return;
      }
      let thumbnailUrlParts = thumbnailUrl.split('/');
      if (thumbnailUrl === this.iconOnvif) {
        const yyyymmdd = moment(this.targetDate).startOf('day').utc().format('YYYYMMDD');
        thumbnailUrlParts = [yyyymmdd, ('00' + hour).slice(-2), ''];
      }
      if (thumbnailUrlParts.length >= 3) {
        const ymdh = `${thumbnailUrlParts[thumbnailUrlParts.length - 3]}${thumbnailUrlParts[thumbnailUrlParts.length - 2]}`;
        if (ymdh.match(/[0-9]{10}/)) {
          this.loadingFlg = true;
          this.$vs.loading({ container: "#camera-center-area", });
          const timeString = ymdh.replace(/([0-9]{4})([0-9]{2})([0-9]{2})([0-9]{2})/, '$1-$2-$3T$4:00:00.000Z');
          this.targetDate = moment(timeString).toDate();
          this.selectedHour = hour;
          this.isHistoryPlayback = true; 
        }
      }
    },

    // 時系列サムネイルリストの生成
    refreshThumbnailUrlList: _.debounce(refreshThumbnailUrlList, 400),
    isExistsThumbnail,
    generateThumbnailPatchUp,
    sleep,


    // パンくずリストのカスタマイズ
    customizeBreadcrumb() {
      customizeBreadcrumb(this, this.camera.name);
    },

    // GCS側のPATHを算出
    convertGcsPath,

    // hykeCam の メソッド群
    async hykeCamTimeout() { await hykeCamTimeout(this); },
    async hykeCamResize() { await hykeCamResize(this); },
    async hykeCamLoadStart() { await hykeCamLoadStart(this); },
    async hykeCamLoadedData() { await hykeCamLoadedData(this); },
    async hykeCamScreen() { await hykeCamScreen(this); },


    /*** onvifメソッドの呼び出しサンプル ***START*** */

    /**
     * [onvif]初期パラメータのセットと接続確認.
     */
    onvifInitControl(url) { onvifInitControl(this, url, this.camera.id); },

    /**
     * [onvif]PTZ操作(パターンA:指定方向に指定角度でジャンプ).
     */
    onvifPtzByStep(x, y, z, angle) { onvifPtzByStep(this, x, y, z, angle); },

    /**
     * [onvif]PTZ操作(パターンB:指定方向に指定秒数スクロール).
     */
    onvifPtzByScroll(x, y, z, sec) { onvifPtzByScroll(this, x, y, z, sec); },

    /**
     * [onvif]PTZスクロール動作を停止.
     */
    onvifStopScroll() { onvifStopScroll(this); },

    /**
     * [onvif]カメラホーム位置へジャンプ移動.
     */
    onvifGotoHomePosition() { onvifGotoHomePosition(this); },

    /**
     * [onvif]カメラホーム位置のセット（現座標がホーム位置になる）.
     */
    onvifSetHomePosition() { onvifSetHomePosition(this); },
    onvifSetHomePositionConfirm() { onvifSetHomePositionConfirm(this); },

    /**
     * (おまけ)[onvif]カメラ機器の再起動.
     *
     * (注)動画セッションの再接続のために、
     *     RaspberryPi上での「systemctl restart webcam-handler」コマンド実行が別途必要.
     *     現時点では参考程度のメソッド.
     */
    onvifReboot() { onvifReboot(this); },

    /**
     * (おまけ)[onvif]解像度の変更.
     *
     * (注)解像度の変更反映には切断＋再接続が必要で、
     *     RaspberryPi上での「systemctl restart webcam-handler」コマンド実行が別途必要.
     *     現時点では参考程度のメソッド.
     */
    onvifUpdateResolution() { onvifUpdateResolution(this); },

    /*** onvifメソッドの呼び出しサンプル ***END** */

    // zoomボタンクリック時
    zoomClicked(val) {
      if (this.mode === 'history') {
        if (!isNaN(val)) {
          this.targetZoom = val;
        } else if (val === 'plus' && this.targetZoom + 1 <= this.cameraZoomMax) {
          this.targetZoom++;
        } else if (val === 'minus' && this.targetZoom - 1 >= this.cameraZoomMin) {
          this.targetZoom--;
        }
      } else if (this.onvifEnable && this.onvifActive) {

        if (!isNaN(val)) {

          this.targetZoom = val;

          const diffZoom = val - this.onvifCurrentZoom;
          const direction = diffZoom > 0 ? 1 : -1;
          Vue.$log.debug(`### zoomClicked(${val}) -> `, `this.onvifPtzByScroll(0, 0, ${direction}, 2)`);
          this.onvifPtzByScroll(0, 0, direction, 2);

        } else if (val === 'plus' && this.targetZoom + 1 <= this.cameraZoomMax) {
          this.targetZoom++;
          Vue.$log.debug(`### zoomClicked(${val}) -> `, `this.onvifPtzByScroll(0, 0, 1, 2)`);
          this.onvifPtzByScroll(0, 0, 1, 2);

        } else if (val === 'minus' && this.targetZoom - 1 >= this.cameraZoomMin) {
          this.targetZoom--;
          Vue.$log.debug(`### zoomClicked(${val}) -> `, `this.onvifPtzByScroll(0, 0, -1, 2)`);
          this.onvifPtzByScroll(0, 0, -1, 2);
        }
      }
    },

    setCarouselItemWidth() {
      const carouselWrapEl = this.$el.querySelector(".carousel-wrap");
      this.carouselItemWidth = carouselWrapEl ? (carouselWrapEl.getBoundingClientRect().width / 8 - 4) + 'px' : '160px';
    },

    handleOnvifAccept() {
      this.clearOnvifTimers();
      this.onvifActive = false;
      this.showOnvifDialog = false;
    },

    handleOnvifCancel() {
      this.clearOnvifTimers();
      this.showOnvifDialog = false;
    },

    clearOnvifTimers() {
      if (this._onvifTimeout) {
        clearTimeout(this._onvifTimeout);
        this._onvifTimeout = null;
      }
      if (this._onvifPopupTimeout) {
        clearTimeout(this._onvifPopupTimeout);
        this._onvifPopupTimeout = null;
      }
    },
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";

button.vjs-big-play-button {
  position: absolute;
  top: 45% !important;
  display: block;
}

#camera-view {
  background-color: $aisea-white;

  .vjs-error .vjs-error-display:before {
    font-family: 'Material Icons';
    content: "\E001";
    font-size: 480%;
  }

  .vjs-text {
    z-index: 1;
    font-size: 2em;
    line-height: 1.5em;
    height: 1.63332em;
    display: block;
    position: absolute;
    top: 10px;
    opacity: 1;
    transition: all 0.4s;
  }

  .forward-layer {
    background-color: #F2F2F2;
    z-index: 99;
  }

  .vue-slider-dot-handle {
    background-color: #2F80ED;
    border: 2px solid #2F80ED20;
  }

  .live-icon-block {
    color: #48A0F4;
    border: 2px solid #48A0F4;
    border-radius: 4px;
    box-shadow: 0 0 10px 0 #48A0F480;
  }

  .con-vs-loading {
    z-index: 999;
  }

  .onvif-waiting {
    pointer-events: none;
    text-align: center;
    position: absolute;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 8px solid #E2E2E2;
    border-right-color: transparent;
    -webkit-animation: spin 1s infinite linear;
    -moz-animation: spin 1s infinite linear;
    -ms-animation: spin 1s infinite linear;
  }

  @-webkit-keyframes spin {
    0% {
      -webkit-transform: rotate(0deg);
      opacity: 1;
    }

    50% {
      -webkit-transform: rotate(180deg);
      opacity: 1;
    }

    100% {
      -webkit-transform: rotate(360deg);
      opacity: 1;
    }
  }

  @-moz-keyframes spin {
    0% {
      -moz-transform: rotate(0deg);
      opacity: 1;
    }

    50% {
      -moz-transform: rotate(180deg);
      opacity: 1;
    }

    100% {
      -moz-transform: rotate(360deg);
      opacity: 1;
    }
  }

  @-ms-keyframes spin {
    0% {
      -ms-transform: rotate(0deg);
      opacity: 1;
    }

    50% {
      -ms-transform: rotate(180deg);
      opacity: 1;
    }

    100% {
      -ms-transform: rotate(360deg);
      opacity: 1;
    }
  }
}

progress {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border-radius: 2px;
  width: 100%;
  height: 8.4px;
  pointer-events: none;
  position: absolute;
  top: 0;
}

progress::-webkit-progress-bar {
  background-color: #474545;
  border-radius: 2px;
}

progress::-webkit-progress-value {
  background-color: #474545;
  border-radius: 2px;
}

.seek {
  position: absolute;
  top: 0;
  width: 100%;
  cursor: pointer;
  margin: 0;
}

.seek:hover+.seek-tooltip {
  display: block;
}

.seek-tooltip {
  display: none;
  position: absolute;
  top: -50px;
  margin-left: -20px;
  font-size: 12px;
  padding: 3px;
  content: attr(data-title);
  font-weight: bold;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.6);
}

.bottom-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.6rem;
}

.left-controls {
  display: flex;
  align-items: center;
  color: #fff;
}

.right-controls {
  display: flex;
  align-items: center;
  color: #fff;
}

.fullscreen-button {
  margin-right: 0;
}

.playback-animation {
  pointer-events: none;
  position: absolute;
  top: 50%;
  left: 50%;
  margin-left: -40px;
  margin-top: -40px;
  width: 80px;
  height: 80px;
  border-radius: 80px;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
}

#custom-breadcrumb-title {
  display: flex;
  align-items: center;
  position: absolute;
  padding: 0;
  margin: 0;
}
</style>
